import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import WorkspaceLayout from "@/components/WorkspaceLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, X, Building2, GraduationCap, Sparkles } from "lucide-react";

export default function MyRecordsPage() {
  const [matches, setMatches] = useState([]);
  const [linked, setLinked] = useState({ employees: [], students: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [m, r] = await Promise.all([
        api.get("/claims/potential-matches"),
        api.get("/records/mine"),
      ]);
      setMatches(m.data.matches || []);
      setLinked(r.data || { employees: [], students: [] });
    } catch (e) {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const claim = async (kind, id) => {
    try {
      await api.post(`/claims/${kind}/${id}/request`);
      toast.success("Claim submitted. Awaiting issuer approval.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const dismiss = async (kind, id) => {
    try {
      await api.post(`/claims/${kind}/${id}/dismiss`);
      toast("Match dismissed");
      load();
    } catch (e) {}
  };

  return (
    <WorkspaceLayout subtitle="Your records" title="Linked records & suggested matches">
      {matches.length > 0 && (
        <Card className="rounded-sm border-ec-emerald/40 bg-ec-emerald/5 mb-8" data-testid="suggested-matches-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-ec-emerald" />
              <div className="eyebrow text-ec-emerald">Smart matches</div>
            </div>
            <h2 className="font-display text-2xl font-bold mt-1">
              {matches.length} record{matches.length === 1 ? "" : "s"} may belong to you
            </h2>
            <p className="text-sm text-ec-ink-soft mt-1">
              These records were uploaded by organizations or institutions and match your contact details. Claim what's yours — only your account will be linked after approval.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              {matches.map((m) => (
                <div key={m.id} className="bg-white border border-ec-line p-4 rounded-sm" data-testid={`match-${m.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <Badge className="rounded-sm bg-ec-mute border-ec-line text-ec-ink">
                        {m.record_kind === "employee" ? <Building2 className="w-3 h-3 inline mr-1" /> : <GraduationCap className="w-3 h-3 inline mr-1" />}
                        {m.record_kind === "employee" ? "Employment" : "Academic"}
                      </Badge>
                    </div>
                    <Badge className="rounded-sm bg-amber-500/10 text-amber-700 border border-amber-500/40">{m.status}</Badge>
                  </div>
                  <div className="font-display font-bold">{m.issuer_name}</div>
                  <div className="text-sm text-ec-ink-soft mt-1">
                    {m.record_kind === "employee"
                      ? `${m.designation} · ${m.department}`
                      : `${m.course} · ${m.batch}`}
                  </div>
                  <div className="text-xs text-ec-ink-soft font-mono-plex mt-1">
                    {m.record_kind === "employee"
                      ? `Joined ${m.joining_date}`
                      : `${m.admission_year} – ${m.graduation_year}`}
                  </div>
                  <div className="mt-4 flex gap-2">
                    {m.status === "Unclaimed" ? (
                      <Button size="sm" className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 gap-1" onClick={() => claim(m.record_kind, m.id)} data-testid={`claim-${m.id}`}>
                        <CheckCircle2 className="w-3 h-3" /> Claim Record
                      </Button>
                    ) : (
                      <Badge className="rounded-sm bg-ec-mute border border-ec-line">Awaiting issuer approval</Badge>
                    )}
                    <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={() => dismiss(m.record_kind, m.id)} data-testid={`dismiss-${m.id}`}>
                      <X className="w-3 h-3" /> Not Mine
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Section title="Linked employment records" icon={Building2} rows={linked.employees} kind="employee" loading={loading} />
      <Section title="Linked academic records" icon={GraduationCap} rows={linked.students} kind="student" loading={loading} />
    </WorkspaceLayout>
  );
}

function Section({ title, icon: Icon, rows, kind, loading }) {
  return (
    <div className="mb-10">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-ec-emerald" />
        <h3 className="font-display text-xl font-bold">{title}</h3>
        <span className="text-xs text-ec-ink-soft font-mono-plex">· {rows.length}</span>
      </div>
      {loading ? (
        <Card className="rounded-sm border-ec-line"><CardContent className="py-10 text-center text-ec-ink-soft">Loading…</CardContent></Card>
      ) : rows.length === 0 ? (
        <Card className="rounded-sm border-ec-line">
          <CardContent className="py-10 text-center text-ec-ink-soft" data-testid={`linked-${kind}-empty`}>
            No linked records yet.
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {rows.map((r) => (
            <Card key={r.id} className="rounded-sm border-ec-line" data-testid={`linked-${kind}-${r.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="font-display font-bold">{r.issuer_name}</div>
                  <Badge className="rounded-sm bg-ec-emerald/10 text-ec-emerald border-ec-emerald/30">Verified</Badge>
                </div>
                <div className="text-sm text-ec-ink-soft mt-1">
                  {kind === "employee" ? `${r.designation} · ${r.department}` : `${r.course} · ${r.batch}`}
                </div>
                <div className="text-xs text-ec-ink-soft font-mono-plex mt-1">
                  {kind === "employee" ? `From ${r.joining_date} · ${r.employment_status}` : `${r.admission_year}–${r.graduation_year} · ${r.student_status}`}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
